<template>
  <div class="licence-setting-editor">
    <v-alert v-if="!loading && !subscriptions.length && !hideNoSubscription" type="info" dense text>{{ $t('onboarding.configuration.licence.no-subscription') }}</v-alert>
    <p v-if="!kinds.length && !hideKind" class="text-caption text--secondary mb-0">{{ $t('onboarding.configuration.licence.no-kind') }}</p>

    <!-- Une ligne par type d'équipement qui porte une licence. -->
    <v-row v-for="kind in kinds" :key="kind" dense align="center">
      <!-- Dans une carte en demi-largeur : l'équipement en titre, l'abonnement sur toute la ligne (son nom est long). -->
      <v-col v-if="!hideKind" cols="12" class="text-subtitle-2 pb-0">{{ $t(`onboarding.commissioning.kind.${kind}`) }}</v-col>

      <!-- Un véhicule : une formule connectable de l'organisation, qui dit aussi son abonnement et sa connexion. -->
      <v-col v-if="isVehicle(kind)" cols="12">
        <!-- Aucune formule connectable pour ce type : grisé, avec la raison. -->
        <v-tooltip bottom :disabled="loading || vehicleFormulasFor(kind).length > 0">
          <template #activator="{ on, attrs }">
            <div v-bind="attrs" v-on="on">
              <v-select
                  :value="vehicleFormulaKey(licenceOf(kind))"
                  :items="vehicleFormulasFor(kind)"
                  :label="$t('onboarding.configuration.licence.vehicle-formula')"
                  :disabled="!loading && !vehicleFormulasFor(kind).length"
                  :loading="loading"
                  outlined dense clearable hide-details
                  @change="chooseVehicleFormula(kind, $event)"
              />
            </div>
          </template>
          <span>{{ $t('onboarding.configuration.licence.no-vehicle-formula') }}</span>
        </v-tooltip>
      </v-col>

      <template v-else>
        <v-col cols="12">
          <v-select
              :value="licenceOf(kind).subscriptionId"
              :items="subscriptionsFor(kind)"
              :item-text="subscriptionText"
              item-value="id"
              :label="$t('onboarding.configuration.licence.subscription')"
              :loading="loading"
              outlined dense hide-details clearable
              @change="choose(kind, { subscriptionId: $event || null, formulaId: null, optionIds: [] })"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-select
              :value="licenceOf(kind).formulaId"
              :items="formulasFor(kind)"
              :hint="subscriptionOf(kind) && !formulasFor(kind).length ? $t('onboarding.configuration.licence.no-formula') : ''"
              persistent-hint
              item-text="name"
              item-value="id"
              :label="$t('onboarding.configuration.licence.formula')"
              :disabled="!subscriptionOf(kind)"
              outlined dense
              :hide-details="!(subscriptionOf(kind) && !formulasFor(kind).length)"
              @change="choose(kind, { ...licenceOf(kind), formulaId: $event })"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-select
              :value="licenceOf(kind).optionIds"
              :items="subscriptionOf(kind) ? subscriptionOf(kind).options.filter(o => o.enabled !== false) : []"
              item-text="name"
              item-value="id"
              :label="$t('onboarding.configuration.licence.options')"
              :disabled="!subscriptionOf(kind)"
              multiple small-chips outlined dense hide-details
              @change="choose(kind, { ...licenceOf(kind), optionIds: $event })"
          />
        </v-col>
      </template>
      <!-- Le refus du serveur, sous la licence qu'il vise. -->
      <v-col v-if="error" cols="12">
        <div class="error--text text-caption">{{ error }}</div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { LICENCE_TYPES, VEHICLE_KINDS } from './CommissioningMixin'

/**
 * La licence que portera l'équipement, par type d'équipement : l'abonnement de l'organisation, sa
 * formule, ses options — choisis parmi les siens, jamais déduits. Sans licence réglée, une commande
 * de ce type est refusée : pas de mise en service sans licence.
 */
export default {
  name: 'LicenceSettingEditor',

  props: {
    value: { type: [Object, Array], default: () => ({}) },
    /** Les types d'équipement dont on règle la licence. */
    kinds: { type: Array, default: () => [] },
    /** Les abonnements de l'organisation, lus une fois par l'écran. */
    subscriptions: { type: Array, default: () => [] },
    /** Les formules véhicule connectables de l'organisation, lues une fois par l'écran. */
    vehicleFormulas: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    /** Dans la fiche d'un seul équipement, son nom est déjà au-dessus. */
    hideKind: { type: Boolean, default: false },
    /** Restreint les abonnements proposés à ces types de licence (`device` : remboursement, `charging-point` : supervision). */
    licenceTypes: { type: Array, default: null },
    /** Le refus du serveur à la publication, affiché sous la licence. */
    error: { type: String, default: null },
    /** L'écran propose déjà de créer l'abonnement sur place : pas de message « aucun abonnement ». */
    hideNoSubscription: { type: Boolean, default: false },
  },

  methods: {
    isVehicle(kind) {
      return VEHICLE_KINDS.includes(kind)
    },

    vehicleFormulaKey(licence) {
      return licence.subscriptionId && licence.formulaId ? `${licence.subscriptionId}/${licence.formulaId}` : null
    },

    vehicleFormulasFor(kind) {
      return this.vehicleFormulas.filter(f => f.kind === kind).map(f => ({
        value: `${f.subscriptionId}/${f.formulaId}`,
        text: `${f.subscriptionName} · ${f.formulaName} (${this.$t(`onboarding.configuration.licence.connection.${f.type}`)})`,
      }))
    },

    chooseVehicleFormula(kind, key) {
      const formula = this.vehicleFormulas.find(f => f.kind === kind && `${f.subscriptionId}/${f.formulaId}` === key)
      this.choose(kind, formula
        ? { subscriptionId: formula.subscriptionId, formulaId: formula.formulaId, optionIds: [] }
        : { subscriptionId: null, formulaId: null, optionIds: [] })
    },

    licenceOf(kind) {
      const map = Array.isArray(this.value) ? {} : (this.value || {})
      return { subscriptionId: null, formulaId: null, optionIds: [], ...(map[kind] || {}) }
    },

    subscriptionsFor(kind) {
      const types = (LICENCE_TYPES[kind] || []).filter(t => !this.licenceTypes || this.licenceTypes.includes(t))
      return LICENCE_TYPES[kind] ? this.subscriptions.filter(s => types.includes(s.licenceType)) : this.subscriptions
    },

    /** Le nom de l'abonnement, et ce qu'il fait : rembourser ou superviser. */
    subscriptionText(subscription) {
      const key = `onboarding.configuration.licence.type.${subscription.licenceType}`
      return this.$te(key) ? `${subscription.name} · ${this.$t(key)}` : subscription.name
    },

    /** Seules les formules actives qui acceptent cet équipement : les autres seraient refusées à la publication. */
    formulasFor(kind) {
      const accepts = kind === 'meter-device' ? 'acceptsMeterDevice' : 'acceptsChargingPoint'
      return (this.subscriptionOf(kind)?.formulas || []).filter(f => f.enabled !== false && f[accepts])
    },

    subscriptionOf(kind) {
      const id = this.licenceOf(kind).subscriptionId
      return this.subscriptions.find(s => s.id === id) || null
    },

    choose(kind, licence) {
      const map = { ...(Array.isArray(this.value) ? {} : (this.value || {})) }
      if (licence.subscriptionId) {
        map[kind] = licence
      } else {
        delete map[kind]
      }
      this.$emit('change', map)
    },
  },
}
</script>
